import { Fragment, type ReactNode } from 'react';
import { Button, Divider, Flex, Typography, theme } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PiArrowCounterClockwiseBold } from 'react-icons/pi';
import { ROLLBACK_COLORS, STEP_COLORS } from './rollbackColors';

export type RollbackStepState = 'done' | 'current' | 'upcoming';

export interface RollbackStepItem {
  key: string;
  title: string;
  state: RollbackStepState;
  // unsupported by the process (e.g. project based internships)
  disabled?: boolean;
  canRollback?: boolean;
}

interface RollbackStepListProps {
  title: string;
  items: RollbackStepItem[];
  // key of the step whose rollback is being confirmed
  confirmingKey?: string;
  onRollback: (item: RollbackStepItem) => void;
  // the confirm panel, shown under the confirming step
  confirmPanel?: ReactNode;
  footer?: ReactNode;
}

const StepCircle = ({
  state,
  number,
}: {
  state: RollbackStepState;
  number: number;
}) => {
  const { token } = theme.useToken();

  const circleStyle: React.CSSProperties = {
    flex: '0 0 32px',
    width: 32,
    height: 32,
    borderRadius: '50%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (state === 'done') {
    return (
      <span
        style={{
          ...circleStyle,
          background: STEP_COLORS.doneCircle,
          color: STEP_COLORS.doneIcon,
        }}
      >
        <CheckOutlined />
      </span>
    );
  }

  if (state === 'current') {
    return (
      <span
        style={{
          ...circleStyle,
          border: `2px solid ${token.colorPrimary}`,
          background: token.colorBgContainer,
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: token.colorPrimary,
          }}
        />
      </span>
    );
  }

  return (
    <span
      style={{
        ...circleStyle,
        border: `2px dashed ${STEP_COLORS.upcomingBorder}`,
        background: token.colorBgContainer,
        color: STEP_COLORS.mutedText,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {number}
    </span>
  );
};

export const RollbackStepList = ({
  title,
  items,
  confirmingKey,
  onRollback,
  confirmPanel,
  footer,
}: RollbackStepListProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const supportedItems = items.filter((item) => !item.disabled);
  const doneCount = supportedItems.filter(
    (item) => item.state === 'done'
  ).length;

  return (
    <Flex vertical>
      <Divider style={{ fontSize: 18, marginBottom: 0 }}>{title}</Divider>
      <Typography.Text
        style={{
          textAlign: 'center',
          margin: '6px 0 16px',
          fontSize: 13,
          color: STEP_COLORS.mutedText,
        }}
      >
        {t('processRollback.stepsDone', {
          done: doneCount,
          total: supportedItems.length,
        })}
      </Typography.Text>

      {/* the steps share one grid: the title column is as wide as the longest title,
          so the Roll back button always lines up right after it */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '32px minmax(0, max-content) auto',
          columnGap: 16,
        }}
      >
        {items.map((item, index) => {
          const isConfirming = item.key === confirmingKey;
          return (
            <Fragment key={item.key}>
              <div
                style={{
                  gridColumn: '1 / -1',
                  display: 'grid',
                  gridTemplateColumns: 'subgrid',
                  alignItems: 'center',
                  minHeight: 44,
                  padding: '2px 8px',
                  borderRadius: 10,
                  background: isConfirming
                    ? ROLLBACK_COLORS.panelBackground
                    : undefined,
                  opacity: item.disabled ? 0.4 : undefined,
                }}
              >
                <StepCircle state={item.state} number={index + 1} />
                <Flex vertical style={{ minWidth: 0 }}>
                  <Typography.Text
                    style={{
                      fontSize: 15,
                      fontWeight: item.state === 'current' ? 700 : undefined,
                      color:
                        item.state === 'upcoming'
                          ? STEP_COLORS.mutedText
                          : undefined,
                    }}
                  >
                    {item.title}
                  </Typography.Text>
                  {item.disabled && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {t('internshipProcessComp.unsupportedProcess')}
                    </Typography.Text>
                  )}
                  {!item.disabled && item.state === 'current' && (
                    <Typography.Text
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: token.colorPrimary,
                      }}
                    >
                      {t('processRollback.inProgress')}
                    </Typography.Text>
                  )}
                </Flex>
                {item.canRollback && (
                  <Button
                    icon={<PiArrowCounterClockwiseBold />}
                    aria-label={`${t('processRollback.rollback')}: ${item.title}`}
                    onClick={() => onRollback(item)}
                    style={{
                      justifySelf: 'start',
                      fontWeight: 700,
                      color: ROLLBACK_COLORS.buttonText,
                      borderColor: ROLLBACK_COLORS.buttonBorder,
                    }}
                  >
                    {t('processRollback.rollback')}
                  </Button>
                )}
              </div>

              {isConfirming && (
                <div style={{ gridColumn: '1 / -1' }}>{confirmPanel}</div>
              )}

              {index < items.length - 1 && (
                <span
                  style={{
                    gridColumn: '1 / -1',
                    justifySelf: 'start',
                    marginLeft: 23,
                    height: 14,
                    borderLeft: `2px dotted ${STEP_COLORS.connector}`,
                  }}
                />
              )}
            </Fragment>
          );
        })}
      </div>

      {footer}
    </Flex>
  );
};
